import React from 'react';
import {
    ButtonGroup,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerProps,
    Flex,
    Box,
    Spinner,
    Text,
    Drawer,
} from '@cad-arc';
import { FormProvider, useForm } from 'react-hook-form';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { UnitLocationForm, UnitLocationFormType } from './unit-location-form';
import { useUnitLocationForm } from './use-unit-location-api';
import { FormIsLoading } from '../forms/integrated-form-components/form-is-loading';
import { FormLoadFailure } from '../forms/integrated-form-components/form-load-failure';
import { useLocationApi } from '../location-select/hooks/use-location-api';
import { ClearButton } from './clear-button';
import { useApplicationType } from '../common/hooks/use-application-type';

export interface UnitLocationDrawerProps {
    unitId: number;
    portalProps?: DrawerProps['portalProps'];
}

export type UnitLocationDrawerType = typeof UnitLocationDrawer;

export function UnitLocationDrawer({ unitId, portalProps }: UnitLocationDrawerProps): JSX.Element {
    const drawerControls = useDrawerControls();
    const { isFirstResponder } = useApplicationType();

    const form = useForm<UnitLocationFormType>({
        mode: 'onBlur',
    });

    const { formData, submitForm, query, mutation } = useUnitLocationForm(
        unitId,
        drawerControls.close
    );

    const { payForEsriLocation } = useLocationApi();

    const onSubmit = (data: UnitLocationFormType): void => {
        if (form.getFieldState('consolidatedLocationView').isDirty) {
            const location = form.getValues('consolidatedLocationView');
            if (location !== undefined) {
                payForEsriLocation(location);
            }
        }
        submitForm(data);
    };

    return (
        <Drawer
            size="lg"
            isOpen
            onClose={drawerControls.close}
            closeOnOverlayClick={false}
            portalProps={portalProps}
        >
            <IntegratedFormProvider
                query={query}
                mutation={mutation}
                form={form}
                onSubmit={onSubmit}
            >
                <DrawerHeader>
                    {formData && (
                        <Flex w="95%" direction="row" justifyContent="space-between">
                            <Text
                                variant={isFirstResponder ? 'headingLg' : 'headingMd'}
                                fontWeight={isFirstResponder ? 'bold' : 'semibold'}
                            >
                                Unit {formData.callSignOption.label.toUpperCase()} Location
                            </Text>
                            <Box mr="8px">
                                <ClearButton unitId={unitId} />
                            </Box>
                        </Flex>
                    )}
                    <FormIsLoading>
                        <Flex justifyContent="center" alignItems="center">
                            <Spinner />
                        </Flex>
                    </FormIsLoading>
                    <FormLoadFailure>
                        <Text>--</Text>
                    </FormLoadFailure>
                </DrawerHeader>
                <DrawerBody>
                    <FormBody>
                        <FormProvider {...form}>
                            <UnitLocationForm formData={formData} unitId={unitId} />
                        </FormProvider>
                    </FormBody>
                </DrawerBody>
                <DrawerFooter>
                    <ButtonGroup>
                        <FormCancelButton onClick={drawerControls.close} />
                        <FormSaveButton data-testid="unit-location-submit" />
                    </ButtonGroup>
                </DrawerFooter>
            </IntegratedFormProvider>
        </Drawer>
    );
}
